/**
 * "Minha agenda" — o bloco que o orçamentista ganhou em 07/08/2026.
 *
 * ── O motor é o mesmo, e isso não é detalhe
 *
 * Usa `useCrmAgenda`, exatamente o hook da HomeComercial. É o endpoint que o
 * INCIDENTE-AGENDA (#78) deixou pessoal: o servidor decide o escopo pelo token,
 * e o smoke do dia 07/08 provou isso vivo no ar — a mesma chamada devolveu 1
 * tarefa com um token e 9 com o do dono. Nenhum filtro por pessoa é refeito
 * aqui: refazer no cliente seria a segunda régua, e a que vale é a de lá.
 *
 * ── Por que um componente novo, e não o bloco da HomeComercial extraído
 *
 * A HomeComercial está aprovada e a instrução foi não mexer nela. Extrair o
 * bloco de lá mudaria uma tela em uso para servir outra — troca ruim.
 *
 * O custo disso está declarado: a MONTAGEM da lista (juntar tarefas do sistema
 * com eventos do Google, filtrar por faixa, ordenar) existe em dois lugares. O
 * dado e a regra de escopo são um só; o desenho, não. Quando a HomeComercial
 * puder ser tocada, os dois viram um — e aí é uma extração de verdade, com ela
 * como referência.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';

import { EmptyState, HelpTip } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useCrmAgenda } from '@/lib/crm/crm-store';
import { formatAgendamento } from '@/lib/format-date';
// Os canônicos do fuso de Brasília. A HomeComercial tem um `hojeIso()`
// local que é só um alias de `hojeBR()` — aqui se chama o canônico direto.
import { hojeBR, mesAtualBR } from '@/lib/datas-brasilia';

type Faixa = 'hoje' | 'semana' | 'mes';

const FAIXAS: Array<{ id: Faixa; rotulo: string }> = [
  { id: 'hoje', rotulo: 'Hoje' },
  { id: 'semana', rotulo: '7 dias' },
  { id: 'mes', rotulo: 'Mês' },
];

export function MinhaAgendaSection() {
  const navigate = useNavigate();
  const [faixa, setFaixa] = useState<Faixa>('hoje');

  const mesAno = mesAtualBR();
  const { data: agenda } = useCrmAgenda(mesAno);

  const hoje = hojeBR();
  const fimSemana = hojeBR(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));

  const itens = [
    ...(agenda?.tarefas ?? [])
      .filter((t) => !t.concluida)
      .map((t) => ({
        id: t.id,
        titulo: t.titulo,
        data: t.data,
        cardId: t.cardId,
        origem: 'sistema' as const,
      })),
    ...(agenda?.eventosGoogle ?? []).map((e) => ({
      id: `gev-${e.id}`,
      titulo: e.titulo,
      data: e.inicio,
      cardId: null as string | null,
      origem: 'google' as const,
    })),
  ]
    .filter((t) => {
      const d = t.data.slice(0, 10);
      if (faixa === 'hoje') return d === hoje;
      if (faixa === 'semana') return d >= hoje && d <= fimSemana;
      return true;
    })
    .sort((a, b) => a.data.localeCompare(b.data));

  return (
    <section data-testid="orcamentista-minha-agenda">
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          Minha agenda
          <HelpTip texto="Suas tarefas com hora marcada e os compromissos do seu Google Agenda. Só os seus — a agenda de cada pessoa é dela." />
        </p>
        <div className="flex items-center gap-1" role="group" aria-label="Período da agenda">
          {FAIXAS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFaixa(f.id)}
              aria-pressed={faixa === f.id}
              data-testid={`agenda-faixa-${f.id}`}
              className={cn(
                'rounded-pill px-2.5 py-1 text-xs transition-colors',
                faixa === f.id
                  ? 'bg-red-soft dark:bg-red-soft-dark font-medium text-ink-1 dark:text-ink-dark-1'
                  : 'text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm',
              )}
            >
              {f.rotulo}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-line bg-paper-surface shadow-card dark:border-line-dark dark:bg-paper-dark-surface">
        {itens.length === 0 ? (
          <EmptyState
            title="Nada marcado"
            description={
              faixa === 'hoje'
                ? 'Você não tem compromisso com hora marcada para hoje.'
                : 'Nenhum compromisso com hora marcada neste período.'
            }
          />
        ) : (
          itens.map((t) => (
            <div
              key={t.id}
              className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0 dark:border-line-dark"
              data-testid={`agenda-item-${t.id}`}
            >
              {/* O horário sai do mesmo `formatAgendamento` do resto do sistema:
                  14:00 salvo é 14:00 na tela, sem passeio de fuso (#76/#36). */}
              <span className="shrink-0 font-mono text-xs tabular-nums text-ink-2 dark:text-ink-dark-2">
                {formatAgendamento(t.data)}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-ink-1 dark:text-ink-dark-1">
                {t.titulo}
              </span>
              {t.origem === 'google' && (
                <span className="shrink-0 rounded-pill bg-line-soft px-2 py-0.5 text-xs text-ink-2 dark:bg-line-soft-dark dark:text-ink-dark-2">
                  Google
                </span>
              )}
              {t.cardId && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/admin/comercial/funil?abrirCard=${encodeURIComponent(t.cardId!)}`)
                  }
                  className="shrink-0 text-xs text-ink-3 hover:text-ink-1 dark:text-ink-dark-3 dark:hover:text-ink-dark-1"
                >
                  Abrir no funil ›
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
